// Global WebSocket connection tracker to prevent duplicate connections
// This helps manage connections across React re-renders and StrictMode

class WebSocketConnectionTracker {
  constructor() {
    this.activeConnection = null;
    this.connectionId = null;
    this.isConnecting = false;
    this.lastCloseTime = null;
  }

  startConnection(connectionId, force = false) {
    // Allow force connection for iOS Safari issues
    if (force) {
      console.log('🔌 Force starting connection (iOS Safari mode)');
      // Close any existing connection
      if (this.activeConnection && this.activeConnection.readyState < 2) {
        console.log('🔌 Force closing existing connection');
        this.activeConnection.close(1000, 'Force reconnect');
      }
      this.isConnecting = true;
      this.connectionId = connectionId;
      this.lastCloseTime = null;
      return true;
    }

    if (this.isConnecting || (this.activeConnection && this.activeConnection.readyState < 2)) {
      console.log('🚫 Preventing duplicate connection - already connecting or connected');
      return false;
    }

    // Prevent rapid reconnections
    if (this.lastCloseTime) {
      const timeSinceClose = Date.now() - this.lastCloseTime;
      if (timeSinceClose < 1000) {
        console.log('🚫 Preventing rapid reconnection - waiting 1 second');
        return false;
      }
    }

    this.isConnecting = true;
    this.connectionId = connectionId;
    console.log(`🔌 Starting new connection: ${connectionId}`);
    return true;
  }

  setConnection(ws, connectionId) {
    if (this.connectionId !== connectionId) {
      console.log('⚠️ Connection ID mismatch - ignoring');
      return false;
    }

    // If we already have an active connection, close it
    if (this.activeConnection && this.activeConnection.readyState < 2) {
      console.log('🔌 Closing existing connection before setting new one');
      this.activeConnection.close(1000, 'Replacing with new connection');
    }

    this.activeConnection = ws;
    this.isConnecting = false;
    console.log(`✅ Connection registered: ${connectionId}`);
    return true;
  }

  endConnection(connectionId) {
    if (this.connectionId === connectionId) {
      console.log(`🔌 Ending connection: ${connectionId}`);
      this.activeConnection = null;
      this.connectionId = null;
      this.isConnecting = false;
      this.lastCloseTime = Date.now();
    }
  }

  // Manual/forced reconnects call this so the rapid-reconnection guard can't
  // swallow them (forceReconnect closes + reopens within the 1s window).
  reset() {
    this.lastCloseTime = null;
    this.isConnecting = false;
  }

  getActiveConnection() {
    return this.activeConnection;
  }

  isActiveConnection(connectionId) {
    return this.connectionId === connectionId;
  }

  hasActiveConnection() {
    return this.activeConnection && this.activeConnection.readyState < 2;
  }
}

// Create singleton instance
export const wsConnectionTracker = new WebSocketConnectionTracker();

// For debugging in console
if (typeof window !== 'undefined') {
  window._wsConnectionTracker = wsConnectionTracker;
}